import React, { useContext, useEffect, useState } from 'react';
import { NotificationContext } from '../../contexts/NotificationContext';
import { Row, Col, Button, Icon, Card } from '@syneto/compass-react';
import { ServiceContext } from '../../contexts/ServiceContext';
import { Loading } from '../../components/Loading';
import { StoreContext } from '../../contexts/StoreContext';

const MAX_TRIAL_PRODUCTS = 2;

export const SoftwareAccess = () => {
	const { licensingService, settingsService } = useContext(ServiceContext);
	const { pushDangerNotification, pushSuccessfulNotification } = useContext(NotificationContext);
	const { state } = useContext(StoreContext);
	const [loading, setLoading] = useState(true);
	const [requestTrialKeyLoading, setRequestTrialKeyLoading] = useState(false);
	const { loggedInUser } = state;
	const [trialProducts, setTrialProducts] = useState([]);

	const [distributionLinks, setDistributionLinks] = useState(null);

	const requestTrialKey = async () => {
		try {
			setRequestTrialKeyLoading(true);
			const message = await licensingService.createTrialProduct(loggedInUser.email, loggedInUser.company.id);
			pushSuccessfulNotification(message);
			setTrialProducts(await licensingService.getTrialActivationKeys(loggedInUser.email));
		} catch (error) {
			pushDangerNotification(error);
		} finally {
			setRequestTrialKeyLoading(false);
		}
	};

	const DownloadFiles = () => {
		// Define the download items configuration
		const downloadItems = [
			{
				label: 'Installation Manual',
				urlKey: 'virtualApplianceOs4GuideUrl',
				downloadText: 'Download PDF'
			},
			{
				label: 'Virtual Appliance Image',
				urlKey: 'virtualApplianceOs4IsoUrl',
				downloadText: 'Download OVA'
			},
			{
				label: 'MD5 File',
				urlKey: 'virtualApplianceOs4MD5Url',
				downloadText: 'Download MD5'
			}
		];

		const downloadItemsOs5 = [
			{
				label: 'Online Documentation',
				urlKey: 'virtualApplianceOs5GuideUrl',
				downloadText: 'View'
			},
			{
				label: 'Virtual Appliance Image',
				urlKey: 'virtualApplianceOs5IsoUrl',
				downloadText: 'Download OVA'
			},
			{
				label: 'MD5 File',
				urlKey: 'virtualApplianceOs5MD5Url',
				downloadText: 'Download MD5'
			}
		];

		const downloadItemsOs6 = [
			{
				label: 'Online Documentation',
				urlKey: 'os6GuideUrl',
				downloadText: 'View'
			},
			{
				label: 'Installation Guide for SynetoOS 6 with Hyperion',
				urlKey: 'os6HyperionInstallationGuideUrl',
				downloadText: 'View'
			},
			{
				label: 'Installation Guide for SynetoOS 6 with ESXi',
				urlKey: 'os6EsxiInstallationGuideUrl',
				downloadText: 'View'
			},
			{
				label: 'Software Only Image (OVA)',
				urlKey: 'os6OvaUrl',
				downloadText: 'Download OVA',
				description: 'Use to install SynetoOS on ESXi hypervisors'
			},
			{
				label: 'Software Only Image (ISO)',
				urlKey: 'os6IsoUrl',
				downloadText: 'Download ISO',
				description:
					'Use to install SynetoOS with Hyperion on baremetal servers through out-of-band management (IPMI, iDRAC, iLO, etc.)'
			},
			{
				label: 'Software Only Image (USB)',
				urlKey: 'os6UsbUrl',
				downloadText: 'Download IMG',
				description: 'Use to install SynetoOS with Hyperion on baremetal servers via USB stick'
			}
		];


		const renderDownloadRow = (item, rowOptions = {}) => {
			const { isLastInSection = false } = rowOptions;
			const url = distributionLinks?.[item.urlKey];
			const hasValidUrl = url && url !== '';

			const rowClass = [
				'align-items-start',
				'py-2',
				isLastInSection ? 'border-bottom-0' : 'border-bottom border-light',
			]
				.join(' ')
				.trim();

			return (
				<Row key={item.urlKey} className={rowClass}>
					<Col lg={3} md={12} className="mb-2 mb-lg-0">
						<p className="text-muted font-weight-light mb-0">{item.label}</p>
					</Col>
					<Col lg={5} md={12} className="mb-2 mb-lg-0">
						{item.description ? (
							<p className="text-muted font-weight-light small mb-0">{item.description}</p>
						) : (
							<p className="text-muted font-weight-light small mb-0">&nbsp;</p>
						)}
					</Col>
					<Col lg={4} md={12}>
						{hasValidUrl ? (
							<a href={url} target="_blank" rel="noopener noreferrer">
								{item.downloadText}
							</a>
						) : (
							<p className="text-muted font-weight-light mb-0">Url is not available</p>
						)}
					</Col>
				</Row>
			);
		};

		const renderTrialProductRow = (product) => {
			const copyKey = () => {
				navigator.clipboard.writeText(product.productKey);
				pushSuccessfulNotification('Your trial key has been successfully copied to the clipboard.');
			};

			return <Row key={product.id}>
				<p className="text-muted font-weight-light mt-2">
					<span role="button" onClick={copyKey}>
						<b>{product.productKey}&nbsp;
							<Icon name="fa fa-copy" />
						</b>
					</span>
				</p>
			</Row>;
		};

		const renderDownloadSectionCard = (title, items, { fullWidth = false } = {}) => {
			const colProps = fullWidth
				? { xs: 12 }
				: { xs: 12, md: 6 };

			return (
				<Col {...colProps} key={title}>
					<Card
						className="shadow h-100"
						content={
							<>
								<h5 className="text-muted font-weight-light border-bottom pb-3 mb-0">{title}</h5>
								<div className="pt-2">
									{items.map((item, index, list) => { return renderDownloadRow(item, { isLastInSection: index === list.length - 1 }); }
									)}
								</div>
							</>
						}
					/>
				</Col>
			);
		};

		return (
			<div className="mt-5 ms-5 me-5">
				<h1 className="text-muted font-weight-light">Software Access Downloads</h1>

				<Row className="mt-4 g-4">
					{renderDownloadSectionCard('SynetoOS 4', downloadItems)}
					{renderDownloadSectionCard('SynetoOS 6', downloadItemsOs6)}
				</Row>

				{trialProducts.length > 0 && (
					<Card
						className="shadow mt-4"
						content={
							<>
								<h5 className="text-muted font-weight-light border-bottom pb-3 mb-0">Available Trial Keys</h5>
								<div className="pt-2">{trialProducts.map(renderTrialProductRow)}</div>
							</>
						}
					/>
				)}

				<Row className="mt-4 g-4">
					{renderDownloadSectionCard('SynetoOS 5 - for migrations only', downloadItemsOs5, { fullWidth: true })}
				</Row>

				<Button className="my-5" onClick={requestTrialKey} disabled={trialProducts.length >= MAX_TRIAL_PRODUCTS} loading={requestTrialKeyLoading}>Request trial key</Button>
			</div>
		);
	};

	useEffect(() => {
		const email = loggedInUser?.email;
		if (!email) {
			setLoading(false);
			return;
		}

		let cancelled = false;

		(async () => {
			try {
				setLoading(true);

				try {
					const links = await settingsService.getDistributionLinks();
					if (!cancelled) {
						setDistributionLinks(links);
					}
				} catch (error) {
					console.log(error);
					if (!cancelled) {
						setDistributionLinks(null);
						pushDangerNotification('Could not load software download links.');
					}
				}

				try {
					const trialKeys = await licensingService.getTrialActivationKeys(email);
					if (!cancelled) {
						setTrialProducts(Array.isArray(trialKeys) ? trialKeys : []);
					}
				} catch (error) {
					console.log(error);
					if (!cancelled) {
						pushDangerNotification('Could not load trial activation keys.');
					}
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [loggedInUser?.email]);

	return loading ? <Loading /> : DownloadFiles();
};
